// Pops Culture — Master Application Bootstrap & View Controller
import './style.css';
import { type Mission, type MaydayBeacon, type DadProfile, type ConcernReport, type BannedEmail, getDadDisplayName } from './types';
import { getCurrentRoute, subscribeRoute, navigateTo, type RoutePath } from './router';
import { getCurrentProfile, subscribeAuth, isAuthenticated, logoutUser } from './services/authService';
import { subscribeMissions, filterMissions } from './services/missionService';
import { subscribeMaydays } from './services/maydayService';
import { subscribeUsers } from './services/userService';

// Components
import { renderLandingPage, bindLandingPageEvents } from './components/LandingPage';
import { renderTermsPage, bindTermsPageEvents } from './components/TermsPage';
import { renderAppNavigation, bindAppNavigationEvents, type AppTab } from './components/Navigation';
import { renderMissionFeed, bindMissionFeedEvents, type MissionFeedFilters } from './components/MissionFeed';
import { renderMaydayBeaconView, bindMaydayBeaconEvents } from './components/MaydayBeacon';
import { renderAdminDashboard, bindAdminDashboardEvents } from './components/AdminDashboard';

import { renderHonorRollView, bindHonorRollEvents } from './components/HonorRoll';
import { renderDadProfileView, bindDadProfileEvents } from './components/DadProfile';

// Modals
import { renderEventDetailModal, bindEventDetailModalEvents } from './modals/EventDetailModal';
import { renderEnlistModal, bindEnlistModalEvents } from './modals/EnlistModal';
import { renderCreateMissionModal, bindCreateMissionModalEvents } from './modals/CreateMissionModal';
import { renderMaydayModal, bindMaydayModalEvents } from './modals/MaydayModal';

import { renderLegalTermsModal, bindLegalTermsModalEvents } from './modals/LegalTermsModal';
import { renderOnboardingModal, bindOnboardingModalEvents } from './modals/OnboardingModal';
import { renderDadProfileModal, bindDadProfileModalEvents } from './modals/DadProfileModal';
import { renderReportConcernModal, bindReportConcernModalEvents } from './modals/ReportConcernModal';

// Services
import { initTermsListener, subscribeTerms, getCachedTermsConfig, hasAcceptedCurrentTerms } from './services/termsService';
import { initBadgesListener, subscribeBadges, getBadgesMasterList } from './services/badgeService';
import { isAdmin, subscribeReports, subscribeBannedEmails } from './services/adminService';

// State
let currentTab: AppTab = 'feed';
let missions: Mission[] = [];
let maydays: MaydayBeacon[] = [];
let registeredUsers: DadProfile[] = [];
let reports: ConcernReport[] = [];
let bannedEmails: BannedEmail[] = [];
let adminListenersInitialized = false;

let feedFilters: MissionFeedFilters = {
  type: 'all',
  kidPolicy: 'all',
  search: '',
  zipCode: '',
  radiusMiles: 50
};

function initAdminDataOnce(): void {
  if (adminListenersInitialized || !isAdmin()) return;
  adminListenersInitialized = true;
  subscribeReports(r => {
    reports = r;
    if (getCurrentRoute() === '/admin') render();
  });
  subscribeBannedEmails(b => {
    bannedEmails = b;
    if (getCurrentRoute() === '/admin') render();
  });
}

function render(): void {
  const route = getCurrentRoute();
  const appRoot = document.getElementById('app');
  if (!appRoot) return;

  const profile = getCurrentProfile();
  const currentTerms = getCachedTermsConfig();
  const isAuth = isAuthenticated();

  // Global Mandatory Terms Enforcement:
  // No matter which path they enter on, if logged in and haven't accepted latest terms + media release,
  // allow viewing /terms (to read full agreement) or force them to /app and require acceptance before continuing.
  if (isAuth && profile && !hasAcceptedCurrentTerms(profile, currentTerms.version)) {
    if (route !== '/app' && route !== '/terms') {
      navigateTo('/app', true);
      return;
    }
  }

  // Dedicated Terms of Service Page: /terms
  if (route === '/terms') {
    closeModals();
    const terms = getCachedTermsConfig();
    appRoot.innerHTML = renderTermsPage(terms, isAuth);
    bindTermsPageEvents();
    return;
  }

  // Public Landing Page: /
  if (route === '/') {
    closeModals();
    const activeDadsCount = registeredUsers.filter(u => u.onboarded).length;
    appRoot.innerHTML = `
      ${renderLandingPage(missions.length, activeDadsCount, 100)}
    `;
    bindLandingPageEvents(() => navigateTo('/terms'));
    return;
  }

  // Admin Route: /admin (Strictly restricted to coryliseno@gmail.com)
  if (route === '/admin') {
    if (!isAuthenticated() || !isAdmin()) {
      navigateTo('/', true);
      return;
    }
    initAdminDataOnce();
    appRoot.innerHTML = `
      <div class="min-h-screen bg-[#06070a] text-slate-100 flex flex-col justify-between font-sans">
        <header class="bg-[#06070a]/95 border-b border-white/[0.08] sticky top-0 z-30 backdrop-blur-md">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div class="flex items-center gap-3 cursor-pointer" onclick="window.location.href='/app'">
              <div class="w-10 h-10 rounded-xl bg-transparent border border-amber-400/80 flex items-center justify-center text-amber-400 font-black text-xl shadow-[0_0_12px_rgba(255,183,3,0.3)]">
                <i class="fa-solid fa-shield-halved"></i>
              </div>
              <span class="text-lg font-black tracking-wider text-slate-100 uppercase">Pops <span class="text-amber-400">Culture Admin</span></span>
            </div>
            <a href="/app" class="btn-secondary px-3.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5">
              <i class="fa-solid fa-compass"></i> Go to App
            </a>
          </div>
        </header>

        <main class="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-12">
          ${renderAdminDashboard(reports, missions, registeredUsers, bannedEmails)}
        </main>
      </div>
    `;
    bindAdminDashboardEvents(() => render());
    return;
  }

  // Protected App Route: /app
  if (!isAuthenticated()) {
    navigateTo('/', true);
    return;
  }

  // Gate suspended / under-review accounts immediately
  if (profile && (profile.status === 'suspended' || profile.underReview)) {
    appRoot.innerHTML = `
      <div class="min-h-screen bg-[#06070a] flex flex-col justify-between font-sans text-slate-200">
        <header class="h-16 border-b border-white/[0.08] flex items-center px-4 max-w-7xl mx-auto w-full">
          <div class="flex items-center gap-2">
            <span class="text-lg font-black tracking-wider text-slate-100 uppercase">Pops <span class="text-[#00f0ff]">Culture</span></span>
          </div>
        </header>

        <main class="flex-1 flex items-center justify-center p-4">
          <div class="card-workshop max-w-md w-full p-7 text-center space-y-4 border-amber-500/50 bg-[#090b12] shadow-[0_0_30px_rgba(255,183,3,0.15)] relative">
            <div class="laser-line-amber absolute top-0 left-0 right-0"></div>
            <div class="w-16 h-16 rounded-2xl bg-amber-950/50 border border-amber-500/60 text-amber-400 flex items-center justify-center text-3xl mx-auto shadow-[0_0_15px_rgba(255,183,3,0.25)]">
              <i class="fa-solid fa-user-shield"></i>
            </div>
            <h2 class="text-xl font-black text-slate-100">Account Under Review</h2>
            <p class="text-xs text-slate-300 leading-relaxed">
              A concern was reported regarding your profile. To maintain safety and trust across Pops Culture, your account has been placed temporarily under review by our moderation team.
            </p>
            <div class="p-3.5 rounded-xl bg-amber-950/30 border border-amber-500/40 text-[11px] text-amber-200 text-left space-y-1">
              <strong class="text-amber-300 block">Next Steps:</strong>
              <p>Our administrators are reviewing the concern. You will be notified once the review is resolved. If you need assistance, please contact us at <a href="mailto:support@popsculture.dad" class="underline text-amber-400 font-bold">support@popsculture.dad</a>.</p>
            </div>
            <button id="suspended-logout-btn" class="btn-secondary w-full py-2.5 rounded-xl text-xs font-bold cursor-pointer">
              <i class="fa-solid fa-arrow-right-from-bracket mr-1.5"></i> Sign Out
            </button>
          </div>
        </main>
      </div>
    `;
    document.getElementById('suspended-logout-btn')?.addEventListener('click', async () => {
      await logoutUser();
      navigateTo('/', true);
    });
    return;
  }

  const needsTerms = profile && !hasAcceptedCurrentTerms(profile, currentTerms.version);
  const needsOnboarding = profile && !profile.onboarded;

  // Gating order: 1. Terms of Service, 2. Dad Resume Onboarding
  if (needsTerms) {
    openLegalTermsModal(true);
  } else if (needsOnboarding) {
    openOnboardingModal();
  }

  // If user is admin, initialize live report listeners
  if (isAdmin()) {
    initAdminDataOnce();
  }

  const filteredMissions = filterMissions(missions, {
    type: feedFilters.type,
    kidPolicy: feedFilters.kidPolicy,
    search: feedFilters.search,
    startDate: feedFilters.startDate,
    endDate: feedFilters.endDate,
    zipCode: feedFilters.zipCode,
    radiusMiles: feedFilters.radiusMiles,
    viewerUid: profile?.uid,
    isAdmin: isAdmin()
  });

  const pendingCrewRequestsCount = profile?.crewRequestsReceived?.length || 0;

  appRoot.innerHTML = `
    ${renderAppNavigation(currentTab, maydays.length, pendingCrewRequestsCount)}
    
    <main class="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-12">
      <div id="tab-content-container">
        ${currentTab === 'feed' ? renderMissionFeed(missions, filteredMissions, feedFilters) : ''}
        ${currentTab === 'crew' ? renderMaydayBeaconView(maydays, registeredUsers) : ''}
        ${currentTab === 'badges' ? renderHonorRollView(missions, maydays) : ''}
        ${currentTab === 'profile' ? renderDadProfileView(profile?.crew?.length || 0) : ''}
      </div>
    </main>

    <!-- Persistent Facilitator Footer with Single Terms of Service Link & Optional Admin Link -->
    <footer class="bg-[#06070a] border-t border-white/[0.08] text-slate-400 text-xs py-10 px-4 sm:px-6 lg:px-8 mt-auto relative">
      <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
      <div class="max-w-7xl mx-auto space-y-6">
        <div class="p-4 rounded-xl bg-[#0a0c13] border border-white/[0.08] space-y-2">
          <div class="flex items-center gap-2 text-cyan-400 font-bold text-xs uppercase tracking-wider">
            <i class="fa-solid fa-scale-balanced"></i> Platform Scope & Legal Operating Model
          </div>
          <p class="text-[11px] leading-relaxed text-slate-400">
            <strong class="text-slate-200">Pops Culture is strictly an open communication and social introduction platform.</strong> 
            We provide an online noticeboard facilitating voluntary gatherings and mutual discovery among local fathers. Pops Culture does not inspect, vet, verify, or background-check any user, their trade skills, or tools. Furthermore, Pops Culture does not inspect, engineer, oversee, or approve any project, structure, work site, or physical activity.
          </p>
        </div>
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-white/[0.06] text-[11px] text-slate-500">
          <div class="flex items-center gap-2">
            <span class="font-bold text-slate-300">Pops Culture</span> &copy; 2026. Shoulder-to-Shoulder Community.
          </div>
          <div class="flex items-center gap-4">
            ${isAdmin() ? `
              <button id="footer-admin-link" class="text-amber-400 hover:text-amber-300 font-bold transition-colors flex items-center gap-1.5 cursor-pointer">
                <i class="fa-solid fa-shield-halved text-[11px]"></i> Admin Console
              </button>
            ` : ''}
            <button id="footer-terms-btn" class="hover:text-cyan-400 transition-colors cursor-pointer" title="View full list of Terms and Privacy">Terms & Privacy Policy</button>
          </div>
        </div>
      </div>
    </footer>
  `;

  // Bind Navigation Events
  bindAppNavigationEvents(
    (tab) => {
      currentTab = tab;
      render();
    },
    () => openCreateMissionModal()
  );

  if (currentTab === 'feed') {
    bindMissionFeedEvents(
      (newFilters) => {
        feedFilters = { ...feedFilters, ...newFilters };
        render();
      },
      (eventId) => {
        const mission = missions.find(m => m.id === eventId);
        if (mission) openEventDetailModal(mission);
      },
      () => openMaydayModal(),
      (dadUid) => {
        const targetDad = registeredUsers.find(u => u.uid === dadUid);
        if (targetDad) openDadProfileModal(targetDad);
      },
      (m) => openReportConcernModal('event', m.id, m.title),
      missions
    );
  } else if (currentTab === 'crew') {
    bindMaydayBeaconEvents(
      () => openMaydayModal(),
      (dadUid) => {
        const targetDad = registeredUsers.find(u => u.uid === dadUid);
        if (targetDad) openDadProfileModal(targetDad);
      },
      (tab) => {
        currentTab = tab;
        render();
      },
      () => render()
    );
  } else if (currentTab === 'badges') {
    bindHonorRollEvents();
  } else if (currentTab === 'profile') {
    bindDadProfileEvents(() => openOnboardingModal());
  }

  // Footer triggers
  document.getElementById('footer-terms-btn')?.addEventListener('click', () => navigateTo('/terms'));
  document.getElementById('footer-admin-link')?.addEventListener('click', () => navigateTo('/admin'));
}

// Modal Helpers
function closeModals(): void {
  const profile = getCurrentProfile();
  const currentTerms = getCachedTermsConfig();
  // Prevent closing modals if user has not accepted terms or completed onboarding
  if (profile && !hasAcceptedCurrentTerms(profile, currentTerms.version)) return;
  if (profile && !profile.onboarded) return;
  const root = document.getElementById('modals-root');
  if (root) root.innerHTML = '';
}

function openEventDetailModal(mission: Mission): void {
  const root = document.getElementById('modals-root');
  if (!root) return;

  root.innerHTML = renderEventDetailModal(mission);
  bindEventDetailModalEvents(
    mission,
    () => closeModals(),
    (m) => openEnlistModal(m),
    (dadUid) => {
      const targetDad = registeredUsers.find(u => u.uid === dadUid);
      if (targetDad) openDadProfileModal(targetDad, mission);
    },
    (m) => openReportConcernModal('event', m.id, m.title)
  );
}

function openDadProfileModal(targetUser: DadProfile, returnToMission?: Mission): void {
  const root = document.getElementById('modals-root');
  if (!root) return;

  const badges = getBadgesMasterList();
  root.innerHTML = renderDadProfileModal(targetUser, badges);
  bindDadProfileModalEvents(
    targetUser,
    () => {
      if (returnToMission) {
        openEventDetailModal(returnToMission);
      } else {
        closeModals();
      }
    },
    () => {
      const freshTarget = registeredUsers.find(u => u.uid === targetUser.uid) || targetUser;
      openDadProfileModal(freshTarget, returnToMission);
      render();
    },
    (user) => openReportConcernModal('user', user.uid, getDadDisplayName(user))
  );
}

function openReportConcernModal(type: 'user' | 'event', targetId: string, targetNameOrTitle: string): void {
  const root = document.getElementById('modals-root');
  if (!root) return;

  root.innerHTML = renderReportConcernModal({ type, targetId, targetNameOrTitle });
  bindReportConcernModalEvents(
    { type, targetId, targetNameOrTitle },
    () => closeModals(),
    () => {
      closeModals();
      render();
    }
  );
}

function openEnlistModal(mission: Mission): void {
  const root = document.getElementById('modals-root');
  if (!root) return;

  root.innerHTML = renderEnlistModal(mission);
  bindEnlistModalEvents(
    mission,
    () => {
      closeModals();
      render();
      // Re-open detail to show unlocked address
      const updated = missions.find(m => m.id === mission.id);
      if (updated) openEventDetailModal(updated);
    },
    () => closeModals()
  );
}

function openCreateMissionModal(): void {
  const root = document.getElementById('modals-root');
  if (!root) return;

  root.innerHTML = renderCreateMissionModal();
  bindCreateMissionModalEvents(
    () => {
      closeModals();
      currentTab = 'feed';
      render();
    },
    () => closeModals()
  );
}

function openMaydayModal(): void {
  const root = document.getElementById('modals-root');
  if (!root) return;

  root.innerHTML = renderMaydayModal();
  bindMaydayModalEvents(
    () => {
      closeModals();
      currentTab = 'crew';
      render();
    },
    () => closeModals()
  );
}

function openLegalTermsModal(isMandatory: boolean = false): void {
  const root = document.getElementById('modals-root');
  if (!root) return;

  const currentTerms = getCachedTermsConfig();
  root.innerHTML = renderLegalTermsModal(isMandatory, currentTerms);
  bindLegalTermsModalEvents(
    () => closeModals(),
    (_version) => {
      closeModals();
      render();
    }
  );
}

function openOnboardingModal(): void {
  const root = document.getElementById('modals-root');
  if (!root) return;

  root.innerHTML = renderOnboardingModal();
  bindOnboardingModalEvents(() => {
    closeModals();
    render();
  });
}

// Global Keyboard Handler (Escape to close modals)
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const profile = getCurrentProfile();
    const currentTerms = getCachedTermsConfig();
    if (profile && !hasAcceptedCurrentTerms(profile, currentTerms.version)) return;
    if (profile && !profile.onboarded) return;
    closeModals();
  }
});

// Subscriptions
subscribeRoute(() => render());
subscribeAuth(() => render());

subscribeMissions((updatedMissions) => {
  missions = updatedMissions;
  render();
});

subscribeMaydays((updatedMaydays) => {
  maydays = updatedMaydays;
  render();
});

subscribeUsers((updatedUsers) => {
  registeredUsers = updatedUsers;
  render();
});

initTermsListener();
subscribeTerms(() => render());

initBadgesListener();
subscribeBadges(() => render());

// Initial boot
render();
